<script lang="ts">
	import { getDecimalOfToken, getNumBackInUnits, getPrecisionOfToken } from '$lib/util/util';
	import {
		addFundsInsufficientBalance,
		addFundsToken,
		addFundsTokenAmount,
		processingFundDeposit,
		addFundsAmount,
		smartContractWalletAddress
	} from '../store';
	import { addETHFundsToSCW, addFundsToSCW } from '$lib/web3/actions/scwModifiers';
	import { sanitizeInputs, toBN } from '$lib/util/util';
	import { nativeBalancesEnriched } from '../../store';
	import { tokensAllowedForDeposit } from '../config';
	import type { TokenSymbol } from '$lib/types/tokens';
	import ScTokenDropDown from '../utils/SCTokenDropDown.svelte';
	import ScTokenSelector from '../utils/SCTokenSelector.svelte';
	import ScButton from '../utils/SCButton.svelte';
	import type { SendTransactionResult } from '@wagmi/core';
	import { optimismTokensConfig } from '$lib/tokensConfig';
	import { getParsedError } from '$lib/web3/errorHandling';
	import {
		addErrorNotification,
		addTransactionNotification
	} from '$lib/notifications/notificationManager';
	import { getUserBalances } from '$lib/data/main';
	import { ethers } from 'ethers';
	import BigNumber from 'bignumber.js';
	import { MIN_ETH_BALANCE } from '$lib/web3/contants';
	import { logAnalytcisEvent } from '$lib/util/analytics';

	let showTokenSelector: boolean = false;
	let depositInput: string = '';

	function depositInputChanged() {
		depositInput = sanitizeInputs(depositInput);
		if (depositInput) {
			$addFundsTokenAmount = toBN(depositInput, getDecimalOfToken($addFundsToken));
		} else $addFundsTokenAmount = toBN('0');
	}

	function setMaxAddInput() {
		if ($addFundsToken == 'ETH') {
			let adjusted = new BigNumber($nativeBalancesEnriched[$addFundsToken].balance).minus(
				toBN(MIN_ETH_BALANCE).toString()
			);

			$addFundsTokenAmount = adjusted.isGreaterThan(0)
				? ethers.BigNumber.from(adjusted.toString())
				: ethers.BigNumber.from('0');
			depositInput = getNumBackInUnits(
				$addFundsTokenAmount.toString(),
				getDecimalOfToken($addFundsToken),
				getPrecisionOfToken($addFundsToken)
			);
			return;
		}
		$addFundsTokenAmount = ethers.BigNumber.from($nativeBalancesEnriched[$addFundsToken].balance);
		depositInput = getNumBackInUnits(
			$addFundsTokenAmount.toString(),
			getDecimalOfToken($addFundsToken),
			getPrecisionOfToken($addFundsToken)
		);
	}

	async function executeAddFunds() {
		try {
			$processingFundDeposit = true;
			let tx: SendTransactionResult;
			if ($addFundsToken == 'ETH') {
				tx = await addETHFundsToSCW(
					$smartContractWalletAddress as `0x${string}`,
					$addFundsTokenAmount
				);
			} else {
				tx = await addFundsToSCW(
					optimismTokensConfig[$addFundsToken].address as `0x${string}`,
					$smartContractWalletAddress as `0x${string}`,
					$addFundsTokenAmount
				);
			}
			logAnalytcisEvent('click_sc_wallet_deposit', {
				token: optimismTokensConfig[$addFundsToken].address,
				amount: $addFundsTokenAmount.toString()
			});
			addTransactionNotification(
				'Funds Deposited',
				`${depositInput} ${$addFundsToken} have been transfered from Polynomial wallet to your wallet.`,
				tx,
				() => {
					(depositInput = '0'), ($addFundsTokenAmount = toBN('0'));
					getUserBalances();
					$processingFundDeposit = false;
				}
			);
		} catch (e: any) {
			const { title, description } = getParsedError(e);
			addErrorNotification(title, description, JSON.stringify(e));
			$processingFundDeposit = false;
		}
	}

	function changeToken(token: TokenSymbol) {
		$addFundsToken = token;
		showTokenSelector = false;
		$addFundsTokenAmount = toBN('0');
		depositInput = '';
	}
</script>

<ScTokenDropDown {changeToken} {showTokenSelector} tokensAllowed={$tokensAllowedForDeposit} />
<div>
	<div class="mt-3">
		<div
			class="flex justify-between border-fx-grey-500 border rounded-xl squircle-xl px-5 py-4 items-center w-full"
		>
			<div class=" items-end gap-1">
				<input
					inputmode="decimal"
					autocomplete="off"
					autocorrect="off"
					minlength="1"
					maxlength="79"
					spellcheck="false"
					pattern="^[0-9]*[.,]?[0-9]*$"
					bind:value={depositInput}
					on:input={depositInputChanged}
					type="string"
					placeholder="0"
					class="bg-transparent text-fx-white font-medium outline-none text-2xl max-w-[130px]"
				/>
				{#if depositInput}
					<div class="text-fx-grey-400 text-xs">
						$ {getNumBackInUnits($addFundsAmount.toString(), 18, 2)}
					</div>
				{:else}
					<div class="text-fx-grey-400 text-xs">-</div>
				{/if}
			</div>

			<div>
				<ScTokenSelector bind:showTokenSelector token={$addFundsToken} />
				<div class="text-fx-grey-400 text-xs ml-auto mr-2 flex gap-1.5">
					Balance:
					{#if $nativeBalancesEnriched && $nativeBalancesEnriched[$addFundsToken]}
						<span class="text-fx-grey-200 text-xs"
							>{getNumBackInUnits(
								$nativeBalancesEnriched[$addFundsToken].balance,
								getDecimalOfToken($addFundsToken),
								getPrecisionOfToken($addFundsToken)
							)}</span
						>
						{#if depositInput !== getNumBackInUnits($nativeBalancesEnriched[$addFundsToken].balance, getDecimalOfToken($addFundsToken))}
							<button
								class="cursor-pointer text-xs text-fx-blue underline"
								on:click={setMaxAddInput}
								on:keydown={setMaxAddInput}
							>
								Max
							</button>
						{/if}
					{:else}
						<span class="text-fx-grey-200 text-xs">--</span>
					{/if}
				</div>
			</div>
		</div>

		<ScButton
			input={depositInput}
			fundsAmount={$addFundsAmount}
			validation={$addFundsInsufficientBalance}
			processing={$processingFundDeposit}
			execute={executeAddFunds}
			type={'Deposit'}
		/>
	</div>
</div>
